/* eslint-disable @typescript-eslint/no-unsafe-call */
/**
 * Create Account Step Component
 * Uses centralized validation service for consistency
 */

import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from '../../../../services/serviceTypes';
import type { StepFormMessage } from '../../data-layer/types';
import { setSavedStepData } from '../../data-store/copilotSlice';
import useFormValidation from '../../hooks/useFormValidation';
import { FORM_RESPONSE_TEXTS } from '../../services/responseTextService';
import StepValidationService from '../../services/stepValidationService';
import ErrorBanner from '../ErrorBanner/ErrorBanner';
import ExpandableForm from '../ExpandableForm/ExpandableForm';
import ResponseWithForm from '../ResponseWithForm/ResponseWithForm';
import StepRenderer from '../StepRenderer/StepRenderer';

interface CreateAccountStepProps {
  message: StepFormMessage;
  onFormSubmit: (
    stepName: string,
    formData: Record<string, unknown>,
    isValid: boolean
  ) => void;
  onSkip: (currentStep: string, nextStep: string) => void;
}

/**
 * CreateAccountStep component for handling account creation configuration
 */
function CreateAccountStep({
  message,
  onSkip,
  onFormSubmit,
}: CreateAccountStepProps) {
  const [validationError, setValidationError] = useState<string | null>(null);
  const dispatch = useDispatch();

  // Get persisted form data from Redux for validation
  const persistedFormData = useSelector(
    (state: RootState) => state.copilot.stepFormData.CreateAccount ?? {}
  );

  const isCompletedCheck = useSelector(
    (state: RootState) => state.copilot.navigation.completedSteps ?? {}
  );

  const skippedSteps = useSelector(
    (state: RootState) => state.copilot.navigation.skippedSteps ?? []
  );

  const btnlabel = isCompletedCheck.includes(message.stepName ?? '');
  const isStepCompletedOrSkipped =
    isCompletedCheck.includes('CreateAccount') ||
    skippedSteps.includes('CreateAccount');

  const isInEditMode = useSelector(
    (state: RootState) => state.copilot.ui.mode === 'edit'
  );

  const { validateForm, localFields } = useFormValidation();

  const validateAndContinue = useCallback(async () => {
    setValidationError(null);

    // Use centralized validation service
    const validation = await StepValidationService.validateStep(
      'CreateAccount',
      persistedFormData,
      validateForm
    );

    if (validation.isValid) {
      // Save to savedStepData on successful validation
      dispatch(
        setSavedStepData({
          stepName: message.stepName ?? 'CreateAccount',
          formData: persistedFormData,
        })
      );

      onFormSubmit(
        message.stepName ?? 'CreateAccount',
        persistedFormData,
        validation.isValid
      );
    } else {
      setValidationError(validation.errors[0] || 'Validation failed');
    }

    // In edit mode, don't collapse form after successful submission
    return validation.isValid && !isInEditMode;
  }, [
    persistedFormData,
    validateForm,
    isInEditMode,
    dispatch,
    message.stepName,
    onFormSubmit,
  ]);

  const handleSkipStep = () => {
    onSkip('CreateAccount', 'UpdateAccount');
  };

  return (
    <ResponseWithForm
      responseText={
        !isInEditMode
          ? FORM_RESPONSE_TEXTS.CREATE_ACCOUNT
          : FORM_RESPONSE_TEXTS.CREATE_ACCOUNT_EDIT
      }>
      <ExpandableForm
        stepName={message.stepName ?? 'Account'}
        heading={message.heading}
        buttonLabel={btnlabel ? 'Update' : message.buttonLabel}
        buttonkind='outlined'
        onSave={validateAndContinue}
        onSecondory={handleSkipStep}
        secondryButtonLabel={isInEditMode ? undefined : 'Skip'}
        isSecondaryDisabled={isStepCompletedOrSkipped}>
        <StepRenderer stepName='CreateAccount' validationFields={localFields} />
      </ExpandableForm>
      {validationError && <ErrorBanner message={validationError} />}
    </ResponseWithForm>
  );
}

export default CreateAccountStep;
